import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  CreditCard, Check, Zap, TrendingUp, Crown, 
  Package, ShoppingCart, Loader2, ArrowRight,
  AlertCircle, CheckCircle
} from "lucide-react";

export const BillingPage = () => {
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Modals
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCycle, setSelectedCycle] = useState("monthly");
  const [processingCheckout, setProcessingCheckout] = useState(false);

  // Memoize filtered plans to avoid recalculating on every render
  const paidPlans = useMemo(() => plans.filter(p => p.id !== "free"), [plans]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, usageRes, bundlesRes, txnRes] = await Promise.all([
        apiClient.get("/billing/plans"),
        apiClient.get("/billing/subscription"),
        apiClient.get("/billing/usage"),
        apiClient.get("/billing/credit-bundles"),
        apiClient.get("/billing/transactions")
      ]);
      
      setPlans(plansRes.data);
      setSubscription(subRes.data);
      setUsage(usageRes.data);
      setBundles(bundlesRes.data.bundles || []);
      setTransactions(txnRes.data || []);
    } catch (error) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId, type) => {
    setCheckingPayment(true);
    try {
      const response = await apiClient.get(`/billing/checkout-status/${sessionId}`);
      if (response.data.status === "completed") {
        toast.success(type === "subscription" ? "Subscription activated!" : "Credits added to your account!");
        fetchData();
      } else if (response.data.status === "pending") {
        toast.info("Payment is being processed...");
        // Check again in 3 seconds
        setTimeout(() => checkPaymentStatus(sessionId, type), 3000);
      }
    } catch (error) {
      toast.error("Failed to verify payment");
    } finally {
      setCheckingPayment(false);
      window.history.replaceState({}, "", "/billing");
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    
    // Check for payment callback
    const sessionId = searchParams.get("session_id");
    const type = searchParams.get("type");
    const cancelled = searchParams.get("cancelled");
    
    if (sessionId) {
      checkPaymentStatus(sessionId, type);
    } else if (cancelled) {
      toast.error("Payment was cancelled");
      window.history.replaceState({}, "", "/billing");
    }
  }, [searchParams, fetchData, checkPaymentStatus]);

  const handleUpgrade = async (plan, cycle) => {
    setProcessingCheckout(true);
    try {
      const response = await apiClient.post("/billing/subscribe", {
        plan: plan.id,
        billing_cycle: cycle
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessingCheckout(false);
    }
  };

  const handleBuyCredits = async (bundleId) => {
    setProcessingCheckout(true);
    try {
      const response = await apiClient.post("/billing/purchase-credits", {
        bundle_id: bundleId
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setProcessingCheckout(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription? You'll retain access until the end of your billing period.")) {
      return;
    }
    
    try {
      await apiClient.post("/billing/cancel-subscription");
      toast.success("Subscription cancelled");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
        <p className="text-slate-600">Verifying your payment...</p>
      </div>
    );
  }

  const currentPlan = subscription?.plan_details;
  const isFreePlan = currentPlan?.id === "free";

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800">Billing & Subscription</h1>
        <p className="text-slate-500 mt-1">Manage your plan and credits</p>
      </div>

      {/* Current Plan & Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-orange-500" />
                Current Plan
              </CardTitle>
              <Badge className={isFreePlan ? "bg-slate-100 text-slate-600" : "bg-orange-100 text-orange-600"}>
                {currentPlan?.name}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-3xl font-bold text-slate-800">
                  £{currentPlan?.monthly_price}
                  <span className="text-base font-normal text-slate-500">/month</span>
                </p>
                {subscription?.subscription?.billing_cycle === "annual" && (
                  <p className="text-sm text-emerald-600">Billed annually (saving 2 months)</p>
                )}
              </div>
              
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {currentPlan?.records_per_month} business records/month
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {currentPlan?.max_templates || "Unlimited"} templates
                </li>
                <li className="flex items-center gap-2 text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500" />
                  {currentPlan?.max_campaigns_per_month || "Unlimited"} campaigns/month
                </li>
              </ul>

              <div className="pt-4 flex gap-3">
                {isFreePlan ? (
                  <Button onClick={() => setUpgradeModalOpen(true)} className="flex-1">
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => setUpgradeModalOpen(true)} variant="outline" className="flex-1">
                      Change Plan
                    </Button>
                    <Button onClick={handleCancelSubscription} variant="ghost" className="text-red-500 hover:text-red-600">
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-500" />
              Record Credits
            </CardTitle>
            <CardDescription>Your available business record searches</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Included records used</span>
                  <span className="font-medium">{usage?.records?.used_this_month || 0} / {usage?.records?.included_per_month || 0}</span>
                </div>
                <Progress 
                  value={(usage?.records?.used_this_month / usage?.records?.included_per_month) * 100 || 0} 
                  className="h-2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-slate-800">{usage?.records?.included_remaining || 0}</p>
                  <p className="text-xs text-slate-500">Included remaining</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-600">{usage?.records?.purchased_credits || 0}</p>
                  <p className="text-xs text-slate-500">Purchased credits</p>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-3xl font-bold text-emerald-600">{usage?.records?.total_available || 0}</p>
                <p className="text-sm text-emerald-700">Total records available</p>
              </div>

              {!isFreePlan && (
                <Button onClick={() => setCreditsModalOpen(true)} variant="outline" className="w-full">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy More Credits
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Plans */}
      <Card>
        <CardHeader>
          <CardTitle>Available Plans</CardTitle>
          <CardDescription>Choose the plan that fits your needs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                isCurrentPlan={plan.id === currentPlan?.id}
                onSelect={() => {
                  if (plan.id !== "free" && plan.id !== currentPlan?.id) {
                    setSelectedPlan(plan);
                    setUpgradeModalOpen(true);
                  }
                }}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transaction History */}
      {transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {transactions.slice(0, 10).map((txn) => (
                <div key={txn.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="font-medium text-slate-800">
                      {txn.transaction_type === "subscription" ? "Subscription" : "Credit Purchase"}
                    </p>
                    <p className="text-sm text-slate-500">
                      {new Date(txn.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">£{txn.amount.toFixed(2)}</p>
                    <Badge className={txn.status === "completed" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}>
                      {txn.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upgrade Modal */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-6">
              <div className="bg-slate-100 rounded-lg p-1 flex gap-1">
                <button
                  onClick={() => setSelectedCycle("monthly")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCycle === "monthly" ? "bg-white shadow text-slate-800" : "text-slate-600"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSelectedCycle("annual")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedCycle === "annual" ? "bg-white shadow text-slate-800" : "text-slate-600"
                  }`}
                >
                  Annual
                  <Badge className="ml-2 bg-emerald-100 text-emerald-600">Save 2 months</Badge>
                </button>
              </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {paidPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPlan?.id === plan.id 
                      ? "border-orange-500 bg-orange-50" 
                      : "border-slate-200 hover:border-orange-300"
                  } ${plan.id === currentPlan?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => plan.id !== currentPlan?.id && setSelectedPlan(plan)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{plan.name}</h3>
                    {plan.id === "growth" && (
                      <Badge className="bg-orange-100 text-orange-600">Popular</Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold">
                    £{selectedCycle === "monthly" ? plan.monthly_price : plan.annual_price}
                    <span className="text-sm font-normal text-slate-500">
                      /{selectedCycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </p>
                  <p className="text-sm text-slate-600 mt-2">
                    {plan.records_per_month} records/month
                  </p>
                  {plan.id === currentPlan?.id && (
                    <Badge className="mt-2">Current Plan</Badge>
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpgradeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => selectedPlan && handleUpgrade(selectedPlan, selectedCycle)}
              disabled={!selectedPlan || processingCheckout}
            >
              {processingCheckout ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Continue to Payment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy Credits Modal */}
      <Dialog open={creditsModalOpen} onOpenChange={setCreditsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Buy Record Credits</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            {bundles.length === 0 ? (
              <p className="text-slate-500 text-center py-4">
                Upgrade to a paid plan to purchase credit bundles
              </p>
            ) : (
              bundles.map((bundle) => (
                <div
                  key={bundle.id}
                  className="border rounded-lg p-4 flex items-center justify-between hover:border-orange-300 transition-colors"
                >
                  <div>
                    <p className="font-semibold">{bundle.records} Records</p>
                    <p className="text-sm text-slate-500">
                      £{(bundle.price / bundle.records).toFixed(3)}/record
                    </p>
                  </div>
                  <Button 
                    onClick={() => handleBuyCredits(bundle.id)}
                    disabled={processingCheckout}
                  >
                    £{bundle.price.toFixed(2)}
                  </Button>
                </div>
              ))
            )}
            <p className="text-xs text-slate-400 text-center pt-2">
              Credits expire 12 months from purchase
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PlanCard = ({ plan, isCurrentPlan, onSelect }) => {
  const icons = {
    free: Zap,
    starter: TrendingUp,
    growth: TrendingUp,
    professional: Crown
  };
  const Icon = icons[plan.id] || Zap;
  
  return (
    <div className={`border rounded-lg p-4 ${isCurrentPlan ? "border-orange-500 bg-orange-50" : "border-slate-200"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold">{plan.name}</h3>
        </div>
        {isCurrentPlan && <Badge>Current</Badge>}
      </div>
      
      <p className="text-2xl font-bold mb-3">
        £{plan.monthly_price}
        <span className="text-sm font-normal text-slate-500">/mo</span>
      </p>
      
      <ul className="space-y-1 text-sm text-slate-600 mb-4">
        <li>{plan.records_per_month} records/mo</li>
        <li>{plan.max_templates || "Unlimited"} templates</li>
        <li>{plan.max_contacts || "Unlimited"} contacts</li>
      </ul>
      
      {!isCurrentPlan && plan.id !== "free" && (
        <Button variant="outline" size="sm" className="w-full" onClick={onSelect}>
          {plan.monthly_price > 0 ? "Upgrade" : "Select"}
        </Button>
      )}
    </div>
  );
};

export default BillingPage;
